import { Types } from '@marriott/mi-ui-library';

export interface BadgeConfig {
  badgeText: string;
  badgeIcon?: string;
  badgeVariation: Types.BadgeVariation.Overlay;
  customClass: string;
}

export enum CardStatus {
  ACTIVE = 'Active',
  CANCELED = 'Canceled',
  DEAD = 'Dead',
  EXPIRED = 'Expired',
  FRAUD = 'Fraud',
  INACTIVE = 'Inactive',
  ON_HOLD = 'On Hold',
  LOCKED = 'Locked',
  REISSUED = 'Reissued',
  UNKNOWN = 'Unknown',
}

type StatusConfig = {
  readonly text: string;
  readonly icon?: string;
};
type StatusKey = keyof typeof STATUS_CONFIG_MAP;

export const cardStatuses = Object.values(CardStatus);

const DEFAULT_CUSTOM_CLASS = 'badge-colors--override' as const;

const STATUS_CONFIG_MAP: Record<string, StatusConfig> = {
  active: { text: CardStatus.ACTIVE },
  on_hold: { text: CardStatus.ON_HOLD, icon: 'icon-clock' },
  locked: { text: CardStatus.LOCKED, icon: 'icon-lock' },
  expired: { text: CardStatus.EXPIRED },
  fraud: { text: CardStatus.FRAUD },
  inactive: { text: CardStatus.INACTIVE },
  reissued: { text: CardStatus.REISSUED },
  unknown: { text: CardStatus.UNKNOWN },
} as const;

const createBadgeConfig = (badgeText: string, badgeIcon?: string): BadgeConfig => ({
  badgeText,
  badgeIcon,
  badgeVariation: Types.BadgeVariation.Overlay,
  customClass: DEFAULT_CUSTOM_CLASS,
});

export interface CardStatusLabelMap {
  active?: string;
  on_hold?: string;
  locked?: string;
  expired?: string;
}

export const getCardStatusBadge = (
  cardStatus: string,
  isLocked = false,
  labelMap?: CardStatusLabelMap
): BadgeConfig => {
  if (isLocked) {
    return createBadgeConfig(labelMap?.locked ?? CardStatus.LOCKED, 'icon-lock');
  }

  const normalizedStatus = cardStatus.toLowerCase() as StatusKey;
  const statusConfig = STATUS_CONFIG_MAP[normalizedStatus];

  if (statusConfig) {
    const displayText = labelMap?.[normalizedStatus as keyof CardStatusLabelMap] ?? statusConfig.text;
    return createBadgeConfig(displayText, statusConfig.icon);
  }

  return createBadgeConfig(cardStatus.toUpperCase());
};
